import { useEffect, useRef } from 'react';
import { useRespondentStore } from './answers-store';
import { autosaveSession, getErrorMessage } from '@/lib/api';

interface AutosaveProps {
  slug: string;
  /** Debounce delay (ms) avant d'envoyer une autosave. */
  debounceMs?: number;
}

/**
 * Composant invisible. À la création :
 * - écoute les changements de `answers` du store.
 * - debounce un POST PATCH session avec les réponses courantes.
 * - met à jour `autosaveStatus` : idle → saving → saved (ou error).
 *
 * RGPD :
 * - Le payload ne contient JAMAIS l'identité (un endpoint séparé l'opt-in
 *   gère la collecte). On envoie strictement `{ answers }`.
 * - Le sanitize côté serveur écartera les items non visibles (minimisation).
 * - Pas d'IP / UA stockés côté serveur.
 *
 * Status UI affiché dans le header du parcours.
 */
export function Autosave({ slug, debounceMs = 1200 }: AutosaveProps) {
  // L'autosave doit utiliser le `resumeToken` (opaque hex) — c'est l'URL param
  // attendu côté backend pour PUT /s/:slug/session/:token, pas le sessionId interne.
  const resumeToken = useRespondentStore((s) => s.resumeToken);
  const answers = useRespondentStore((s) => s.answers);
  const setAutosaveStatus = useRespondentStore((s) => s.setAutosaveStatus);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSerializedRef = useRef<string>('');

  useEffect(() => {
    if (!resumeToken) return;
    const serialized = JSON.stringify(answers);
    if (serialized === lastSerializedRef.current) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setAutosaveStatus('saving');
      autosaveSession(slug, resumeToken, { answers })
        .then((res) => {
          lastSerializedRef.current = serialized;
          setAutosaveStatus('saved', res.updatedAt);
        })
        .catch((err: unknown) => {
          // eslint-disable-next-line no-console
          console.warn('[autosave]', getErrorMessage(err));
          setAutosaveStatus('error');
        });
    }, debounceMs);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [answers, resumeToken, slug, debounceMs, setAutosaveStatus]);

  return null;
}

/** Indicateur visuel de statut autosave (à placer dans le header). */
export function AutosaveStatusBadge() {
  const status = useRespondentStore((s) => s.autosaveStatus);

  let text = '';
  switch (status) {
    case 'saving':
      text = 'Sauvegarde en cours…';
      break;
    case 'saved':
      text = 'Sauvegardé';
      break;
    case 'error':
      text = 'Sauvegarde indisponible — nous réessayons.';
      break;
    case 'idle':
    default:
      text = '';
  }

  return (
    <span
      className="text-xs text-text-muted"
      aria-live="polite"
      aria-atomic="true"
    >
      {text}
    </span>
  );
}
